import './styles.scss'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import LayoutContainer from 'Components/LayoutComponent'
import Loader from 'Components/Loader'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'

const AddVolumeAndInterest = ({ setLoading }) => {
  const marketTypeOptions = [
    { marketId: 1, label: 'Robusta' },
    { marketId: 2, label: 'Arabica' }
  ]
  const { t } = useTranslation()
  const history = useHistory()
  const [formData, setFormData] = useState({
    date: '',
    marketType: '',
    volume: '',
    volumeDifference: '',
    interest: '',
    interestDifference: ''
  })

  const [showDialog, setShowDialog] = useState({
    showModal: false,
    modalType: '',
    message: '',
    onHide: () => {},
    onRetry: () => {}
  })

  const [inputValidity, setInputValidity] = useState({
    date: false,
    marketType: false,
    volume: false,
    volumeDifference: false,
    interest: false,
    interestDifference: false
  })

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const formatDateForApi = (date) => {
    if (!date) return ''

    const day = String(date.getDate()).padStart(2, '0')
    const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date?.getMonth()]
    const year = String(date.getFullYear()).slice(-2)

    return `${day}-${month}-${year}`
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const postObject = {
        date: formatDateForApi(formData.date),
        marketId: formData.marketType,
        totalVolume: formData.volume,
        volumeDifference: formData.volumeDifference,
        totalOpenInterest: formData.interest,
        openInterestDifference: formData.interestDifference
      }

      const response = await apiAdapterCoffeeWeb.postVolumeInterestData(postObject)

      if (response.status === 200) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('VOLUME_DATA_ADDED_SUCCESSFULLY'),
          onHide: handleDismiss,
          onSuccess: handleDismiss
        })
      }
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: error?.response?.data?.message || t('FAILED_TO_ADD_DATA'),
        onRetry: handleSubmit,
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const handleBlur = (fieldName) => {
    setInputValidity((prev) => ({
      ...prev,
      [fieldName]: true
    }))
  }

  const handleDismiss = () => {
    history.push({
      pathname: ROUTE_STRINGS.volumnInterest
    })
    setShowDialog({ ...showDialog, showModal: false })
  }

  const isFormValid = formData.date && formData.marketType && formData.volume && formData.volumeDifference && formData.interest && formData.interestDifference

  return (
    <LayoutContainer title={t('ADD_VOLUME_DATA')} breadCrumbsTitle={'addVolumeAndInterest'} showAddButton={false}>
      <div className="add-volume-interest-wrapper">
        <div className="add-volume-fields-row">
          <div className={`form-field ${inputValidity.date && !formData.date ? 'error' : ''}`}>
            <span className="p-float-label">
              <Calendar id="date" value={formData.date} onChange={(e) => handleInputChange('date', e.value)} className="input-control" showIcon dateFormat="dd/mm/yy" onBlur={() => handleBlur('date')} />
              <label htmlFor="date">
                {t('DATE')} <span className="required-label">*</span>
              </label>
            </span>
            {inputValidity.date && !formData.date && <small className="error-text">{t('DATE_IS_REQUIRED')}</small>}
          </div>

          <div className={`form-field ${inputValidity.marketType && !formData.marketType ? 'error' : ''}`}>
            <span className="p-float-label">
              <Dropdown id="marketType" value={formData.marketType} options={marketTypeOptions} onChange={(e) => handleInputChange('marketType', e.value)} optionLabel="label" optionValue="marketId" placeholder={t('SELECT_MARKET_TYPE')} className="input-control" />{' '}
              <label htmlFor="marketType">
                {t('MARKET_TYPE')} <span className="required-label">*</span>
              </label>
            </span>
            {inputValidity.marketType && !formData.marketType && <small className="error-text">{t('Select Market Type')}</small>}
          </div>
        </div>

        <div className="add-volume-fields-row">
          <div className={`form-field ${inputValidity.volume && !formData.volume ? 'error' : ''}`}>
            <span className="p-float-label">
              <InputText id="volume" value={formData.volume} onChange={(e) => handleInputChange('volume', e.target.value)} onBlur={() => handleBlur('volume')} className={`input-control ${inputValidity.volume && !formData.volume ? 'error' : ''}`} />{' '}
              <label htmlFor="volume">
                {t('VOLUME')} <span className="required-label">*</span>
              </label>
            </span>
            {inputValidity.volume && !formData.volume && <small className="error-text">{t('VOLUME_IS_REQUIRED')}</small>}
          </div>

          <div className={`form-field ${inputValidity.volumeDifference && !formData.volumeDifference ? 'error' : ''}`}>
            <span className="p-float-label">
              <InputText id="volumeDifference" value={formData.volumeDifference} onChange={(e) => handleInputChange('volumeDifference', e.target.value)} onBlur={() => handleBlur('volumeDifference')} className={`input-control ${inputValidity.volumeDifference && !formData.volumeDifference ? 'error' : ''}`} />{' '}
              <label htmlFor="volumeDifference">
                {t('VOLUME_DIFFERENCE')} <span className="required-label">*</span>
              </label>
            </span>
            {inputValidity.volumeDifference && !formData.volumeDifference && <small className="error-text">{t('VOLUME_DIFFERENCE_IS_REQUIRED')}</small>}
          </div>
        </div>

        <div className="add-volume-fields-row">
          <div className={`form-field ${inputValidity.interest && !formData.interest ? 'error' : ''}`}>
            <span className="p-float-label">
              <InputText id="interest" value={formData.interest} onChange={(e) => handleInputChange('interest', e.target.value)} onBlur={() => handleBlur('interest')} className={`input-control ${inputValidity.interest && !formData.interest ? 'error' : ''}`} />{' '}
              <label htmlFor="interest">
                {t('INTEREST')} <span className="required-label">*</span>
              </label>
            </span>
            {inputValidity.interest && !formData.interest && <small className="error-text">{t('INTEREST_IS_REQUIRED')}</small>}
          </div>

          <div className={`form-field ${inputValidity.interestDifference && !formData.interestDifference ? 'error' : ''}`}>
            <span className="p-float-label">
              <InputText id="interestDifference" value={formData.interestDifference} onChange={(e) => handleInputChange('interestDifference', e.target.value)} onBlur={() => handleBlur('interestDifference')} className={`input-control ${inputValidity.interestDifference && !formData.interestDifference ? 'error' : ''}`} />{' '}
              <label htmlFor="interestDifference">
                {t('INTEREST_DIFFERENCE')} <span className="required-label">*</span>
              </label>
            </span>
            {inputValidity.interestDifference && !formData.interestDifference && <small className="error-text">{t('INTEREST_DIFFERENCE_IS_REQUIRED')}</small>}
          </div>
        </div>

        <div className="community-form-actions">
          <ButtonComponent label={t('SAVE')} type="submit" onClick={handleSubmit} disabled={!isFormValid} />
        </div>
      </div>
      <DialogModal {...showDialog} onDismiss={handleDismiss} />
    </LayoutContainer>
  )
}

export default Loader(AddVolumeAndInterest)
